import {
  Card,
  Group,
  Text,
  Chip,
  Stack,
  Badge,
  Divider,
  Menu,
} from "@mantine/core";
import { VehicleStopResponse } from "types/trip.types";
import dayjs from "dayjs";
import { IconClock, IconNavigationCheck } from "@tabler/icons-react";
import {
  formatDate,
  formatMinutes,
  formatTime,
  timePart,
} from "utils/string.helpers";
import { ActionFunc, getVehicleStopTypeAndColor } from "./helper";
import { ColorSpectrum } from "types/ColorSpectrum";
import { MouseEvent, useState } from "react";

interface TripVehicleStopCardProps {
  stop: VehicleStopResponse;
  timezone?: string;
  colorSpectrum?: ColorSpectrum;
  current?: boolean;
  adminMode?: boolean;
  hideStopTimes?: boolean;
  onAction?: ActionFunc;
}

export default function TripVehicleStopCard({
  stop,
  timezone,
  colorSpectrum,
  current,
  hideStopTimes,
  onAction,
}: TripVehicleStopCardProps) {
  const arrivedAt = stop.arrivedAt ? timePart(stop.arrivedAt) : "-";
  const durationStr = stop.duration ? formatMinutes(stop.duration) : "-";
  const type = stop.type ?? "UNKNOWN";
  const purpose = stop.purpose ?? "UNKNOWN";
  const dwellCost = stop.dwellCost ?? 0;
  const transitCost = stop.transitShareCost ?? 0;
  const attributedCost = dwellCost + transitCost;
  const stopProperties = getVehicleStopTypeAndColor(stop);
  const [menuOpened, setMenuOpened] = useState(false);

  const handleClick = () => {
    onAction && onAction("focus-on-vehicle-stop", { point: stop, zoom: 18 });
  };

  const handleBadgeClick = (e: MouseEvent) => {
    e.stopPropagation();
    setMenuOpened((v) => !v);
  };

  const handleMenuClose = () => setMenuOpened(false);

  const handleRemove = (e: MouseEvent) => {
    e.stopPropagation();
    setMenuOpened(false);
    onAction && onAction("vehicle-stop-remove-purpose", { stop });
  };

  const handleChange = (e: MouseEvent) => {
    e.stopPropagation();
    setMenuOpened(false);
    onAction && onAction("identify-vehicle-stop", { stop });
  };

  return (
    <Card
      shadow="xs"
      padding="sm"
      radius="md"
      withBorder
      onClick={handleClick}
      style={{
        borderColor: current ? "var(--mantine-color-dark-3)" : undefined,
        backgroundColor: current ? "var(--mantine-color-dark-5)" : undefined,
      }}
    >
      <Stack gap="xs" style={{ cursor: "pointer" }}>
        {
          // region Date&Time
        }
        <Group justify="space-between">
          <Group gap={"xs"}>
            {stop.additionalData?.stopLabel && (
              <div
                style={{
                  height: 24,
                  width: 24,
                  border: "1px solid",
                  borderRadius: "50%",
                  textAlign: "center",
                  lineHeight: "22px",
                  fontSize: "75%",
                  fontWeight: "bold",
                  backgroundColor: colorSpectrum?.main,
                  borderColor: colorSpectrum?.dark40,
                  color: colorSpectrum?.dark40,
                }}
              >
                {stop.additionalData?.stopLabel}
              </div>
            )}
            {!hideStopTimes && (
              <Text size="xs" c="dimmed">
                <IconNavigationCheck
                  size={14}
                  style={{ verticalAlign: "middle", marginRight: 4 }}
                />
                {arrivedAt}
              </Text>
            )}
          </Group>
          {!hideStopTimes && (
            <Text size="xs" c="dimmed">
              <IconClock
                size={14}
                style={{ verticalAlign: "middle", marginRight: 4 }}
              />
              {durationStr}
            </Text>
          )}
        </Group>
        {
          // region Customer&Type
        }
        <Group gap="xs" justify="space-between">
          <Text size="sm">{stopProperties.name || type}</Text>
          <Menu
            opened={menuOpened}
            onClose={handleMenuClose}
            position="bottom-end"
            shadow="md"
            withArrow
            arrowSize={12}
            withOverlay
            // disabled={stop.type === "HUB"}
          >
            <Menu.Target>
              <Badge
                size="sm"
                color={stopProperties.color}
                variant="outline"
                style={{ cursor: "pointer" }}
                onClick={stop.type === "HUB" ? undefined : handleBadgeClick}
              >
                {stopProperties.type}
              </Badge>
            </Menu.Target>
            {Boolean(stop.purpose) ? (
              <Menu.Dropdown>
                <Menu.Item onClick={handleRemove}>Remove</Menu.Item>
                <Menu.Item onClick={handleChange}>Change</Menu.Item>
              </Menu.Dropdown>
            ) : (
              <Menu.Dropdown>
                <Menu.Item onClick={handleChange}>Identify</Menu.Item>
              </Menu.Dropdown>
            )}
          </Menu>
        </Group>
        {
          // region Cost
        }
        <Group gap="md">
          <Text size="sm">
            Attributed Cost: <b>${attributedCost.toFixed(2)}</b>
          </Text>
        </Group>
        <Group gap="md">
          <Text size="xs" c="dimmed">
            Dwell Cost: ${dwellCost.toFixed(2)}
          </Text>
          <Text size="xs" c="dimmed">
            Transit Cost: ${transitCost.toFixed(2)}
          </Text>
        </Group>
        {
          // region Orders
        }
        {Boolean(stop.orders?.length) && (
          <>
            <Divider />
            {stop.orders?.map((order) => (
              <Text size="xs">{order.externalId}</Text>
            ))}
          </>
        )}
      </Stack>
    </Card>
  );
}
